import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router";
import { cultureDetails as bio } from "../assets/componentDetails";
import { cultivatorsEventPanels } from "../assets/cultivatorsEventPanels";
import { cultureTabs as tabs } from "../assets/pageTabs";
import CardsGrid from "../components/CardsGrid";
import GridController from "../components/GridController";
import Header from "../components/Header";
import Loading from "../components/Loading";
import PodcastSeriesGrid from "../components/PodcastSeriesGrid";
import useHomeFeed from "../hooks/useHomeFeed";
import usePageReset from "../hooks/usePageReset";
import structuredData from "../assets/structuredText";

export default function Culture() {
  const { cultureTab } = useParams();
  const [displayed, setDisplayed] = useState<string>((cultureTab as string) || "article");
  const navigate = useNavigate();
  const chooseTab = useCallback(
    (id: string) => {
      setDisplayed(id);
    },
    [setDisplayed]
  );

  // const [fetchedCards, isLoading] = useCultureTabs(displayed);
  const [fetchedCards, isLoading] = useHomeFeed(undefined, displayed);

  useEffect(() => {
    const tabFound = tabs.find((tab) => tab.id === cultureTab);
    if (tabFound) setDisplayed(cultureTab as string);
    if (!tabFound && cultureTab) navigate(`/404`);
  }, [cultureTab, setDisplayed]);

  usePageReset();

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="keywords" content="events articles posts news" />
        <meta
          name="description"
          content="Understand how Cultiv's culture fosters collaboration, creativity, and a
supportive environment for entrepreneurs and innovators."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:url" content="https://cultiv.llc" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          itemProp="image"
          content="https://cultiv.llc/images/metaimg.jpg"
        />
        <link rel="image_src" itemProp="image" href="https://cultiv.llc/images/metaimg.jpg" />
        <meta
          property="og:description"
          content=" Understand how Cultiv's culture fosters collaboration, creativity, and a
supportive environment for entrepreneurs and innovators."
        />
        <meta property="og:site_name" content="Cultiv LLC" />
        <meta property="og:title" content="Cultiv | Culture" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://cultiv.llc" />
        <meta property="twitter:title" content="Cultiv | Culture" />
        <meta
          property="twitter:description"
          content=" Understand how Cultiv's culture fosters collaboration, creativity, and a
supportive environment for entrepreneurs and innovators."
        />
        <meta property="twitter:image" content="https://cultiv.llc/images/metaimg.jpg" />
        <link rel="canonical" href="https://cultiv.llc" />
        <script type="application/ld+json">{structuredData}</script>
        <title>Culture</title>
      </Helmet>

      <div className="w-full flex flex-col justify-center items-center lg:gap-10 gap-6">
        <Header desc={bio.text} color="bg-cultiv-red" />
        <div className="w-full flex flex-col lg:gap-10 gap-6 justify-center items-center  md:px-15 lg:px-20 xl:px-30 px-6">
          <GridController
            displayed={displayed}
            setDisplayed={chooseTab}
            tabs={tabs}
            page="culture"
          />
          {displayed !== "podcasts" ? (
            isLoading ? (
              <div className="min-h-[50vh] sm:min-h-[70vh] flex items-center justify-center">
                <Loading />
              </div>
            ) : (
              <CardsGrid
                cards={
                  displayed == "event" ? [...cultivatorsEventPanels, ...fetchedCards] : fetchedCards
                }
              />
            )
          ) : (
            <PodcastSeriesGrid />
          )}
        </div>
      </div>
    </>
  );
}
